import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { LoggingService, LogLevel } from './logging.service';

export interface CircuitBreakerConfig {
  failureThreshold: number;
  recoveryTimeout: number;
}

export type CircuitBreakerState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

export interface CircuitBreakerStatus {
  state: CircuitBreakerState;
  failures: number;
  lastFailure: number;
  threshold: number;
}

@Injectable({
  providedIn: 'root'
})
export class CircuitBreakerService {
  private state: CircuitBreakerState = 'CLOSED';
  private failures = 0;
  private lastFailure = 0;
  private recoveryTimeout: any = null;
  private config: CircuitBreakerConfig = { failureThreshold: 5, recoveryTimeout: 60000 };

  // Event emitter for state changes
  private stateChange$ = new Subject<{
    state: CircuitBreakerState;
    reason: string;
    timestamp: number;
  }>();

  constructor(private loggingService: LoggingService) {}

  configure(config: CircuitBreakerConfig): void {
    this.config = { ...config };
  }

  // Check if operation should be allowed
  canExecute(): boolean {
    return this.state !== 'OPEN';
  }

  // Record a successful operation
  recordSuccess(): void {
    if (this.state === 'HALF_OPEN') {
      this.state = 'CLOSED';
      this.failures = 0;
      this.loggingService.log(LogLevel.WARN, 'CircuitBreakerService',
        'Circuit breaker reset to CLOSED after successful operation');
      this.emitStateChange('CLOSED', 'success_in_half_open');
    }
  }

  // Record a failed operation
  recordFailure(): void {
    this.failures++;
    this.lastFailure = Date.now();

    if (this.failures >= this.config.failureThreshold && this.state === 'CLOSED') {
      this.state = 'OPEN';
      this.loggingService.log(LogLevel.ERROR, 'CircuitBreakerService',
        `Circuit breaker OPEN after ${this.failures} failures`);
      this.emitStateChange('OPEN', 'failure_threshold_exceeded');

      // Schedule recovery attempt
      this.scheduleRecovery();
    }
  }

  // Get current status
  getStatus(): CircuitBreakerStatus {
    return {
      state: this.state,
      failures: this.failures,
      lastFailure: this.lastFailure,
      threshold: this.config.failureThreshold
    };
  }

  // Observable for state changes
  get stateChanges$() {
    return this.stateChange$.asObservable();
  }

  // Manual reset (for testing/admin purposes)
  reset(): void {
    this.state = 'CLOSED';
    this.failures = 0;
    this.lastFailure = 0;
    this.clearRecoveryTimeout();
    this.loggingService.log(LogLevel.WARN, 'CircuitBreakerService',
      'Circuit breaker manually reset');
    this.emitStateChange('CLOSED', 'manual_reset');
  }

  // Cleanup resources
  destroy(): void {
    this.clearRecoveryTimeout();
    this.stateChange$.complete();
  }

  private scheduleRecovery(): void {
    this.clearRecoveryTimeout();
    
    this.recoveryTimeout = setTimeout(() => {
      this.state = 'HALF_OPEN';
      this.loggingService.log(LogLevel.WARN, 'CircuitBreakerService',
        'Circuit breaker moved to HALF_OPEN for recovery test');
      this.emitStateChange('HALF_OPEN', 'recovery_timeout');
    }, this.config.recoveryTimeout);
  }

  private clearRecoveryTimeout(): void {
    if (this.recoveryTimeout) {
      clearTimeout(this.recoveryTimeout);
      this.recoveryTimeout = null;
    }
  }

  private emitStateChange(state: CircuitBreakerState, reason: string): void {
    this.stateChange$.next({
      state,
      reason,
      timestamp: Date.now()
    });
  }
}
